import {ssrSafeLocation} from '@github-ui/ssr-utils'

/** Formats a large number with K/M suffixes for display. */
export function socialCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}m`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}k`
  return String(count)
}

/** Formats a number with locale-specific thousand separators. */
export function numberWithDelimiter(n: number): string {
  return n.toLocaleString()
}

/**
 * Calculates how many items to show, matching Rails SidebarListComponent logic.
 * - If count <= maxCount: show all
 * - If count == maxCount + 1: show all (avoids a silly "+ 1 more" link)
 * - If count > maxCount + 1: show maxCount
 */
export function getItemsToShow(count: number, maxCount: number): number {
  if (count < maxCount) return count
  if (count === maxCount + 1) return maxCount + 1
  return maxCount
}

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

/** Maximum length for newly-created repository descriptions. Legacy repos may exceed this. */
export const MAX_DESCRIPTION_LENGTH = 350

/**
 * Truncates a repository description to `MAX_DESCRIPTION_LENGTH` characters, appending an
 * ellipsis when shortened. Ensures mobile and desktop render the same visible text even when
 * a legacy repo's description exceeds the current 350-char limit.
 */
export function truncateDescription(text: string, limit: number = MAX_DESCRIPTION_LENGTH): string {
  if (text.length <= limit) return text
  if (limit <= 0) return ''
  if (limit === 1) return '…'
  return `${text.slice(0, limit - 1).trimEnd()}…`
}

/**
 * Returns the URL if it uses a safe protocol (http/https/mailto) or is an anchor (#),
 * otherwise returns undefined. Prevents javascript: and data: scheme injection.
 */
export function safeHref(url: string | undefined): string | undefined {
  if (!url) return undefined
  if (url.startsWith('#')) return url
  try {
    const parsed = new URL(url, ssrSafeLocation.origin)
    return SAFE_PROTOCOLS.has(parsed.protocol) ? url : undefined
  } catch {
    return undefined
  }
}
